import dayjs from "dayjs";
import {
  EXERCISE_SESSION_CODE,
  SCREEN_TIME_CODE,
  formatWearableObservationValue,
  getObservationComponentRows,
  getObservationPrimaryCode,
  getSleepSkinTempDisplay,
  getSleepTotalDisplay,
  getSpO2DisplayParts,
  getWearableObservationLabel,
} from "@/utils/wearableObservationFormat";
import { sortByMetricLabel } from "./metricDisplayOrder";

/** Uma medição simples: rótulo, valor e (quando útil) uma linha de apoio. */
export interface MetricRow {
  id: string;
  label: string;
  value: string;
  hint?: string;
}

/** Bloco com várias medições relacionadas (sessão de exercício, tempo de tela). */
export interface DetailGroup {
  id: string;
  title: string;
  period?: string;
  metrics: MetricRow[];
}

/** Horário/período legível da Observation, usado como apoio nos blocos. */
function periodLabel(resource: any): string | undefined {
  const start = resource?.effectivePeriod?.start ?? resource?.effectiveDateTime;
  const end = resource?.effectivePeriod?.end;
  if (!start) return undefined;

  const startText = dayjs(start).format("HH:mm");
  if (!end) return `A partir de ${startText}`;
  return `${startText} — ${dayjs(end).format("HH:mm")}`;
}

/**
 * Traduz as Observations de uma seção em linhas simples e blocos de detalhe.
 * A separação é o que permite a leitura em lista (mais compacta) em vez de
 * um cartão grande por medição.
 */
export function buildSectionView(entries: any[]): {
  metrics: MetricRow[];
  details: DetailGroup[];
} {
  const metrics: MetricRow[] = [];
  const details: DetailGroup[] = [];

  entries.forEach((entry: any, index: number) => {
    const resource = entry?.resource;
    const id = resource?.id || entry?.fullUrl || `obs-${index}`;
    const code = getObservationPrimaryCode(resource);
    const label = getWearableObservationLabel(resource);

    // Sessão de exercício e tempo de tela têm vários components aninhados
    if (code === EXERCISE_SESSION_CODE || code === SCREEN_TIME_CODE) {
      const rows = getObservationComponentRows(resource);
      const typeRow =
        code === EXERCISE_SESSION_CODE
          ? rows.find((row) =>
              row.label.toLowerCase().includes("tipo de exercício")
            )
          : undefined;

      details.push({
        id,
        title: typeRow ? typeRow.value : label,
        period: periodLabel(resource),
        metrics: rows
          .filter((row) => row !== typeRow)
          .map((row) => ({
            id: `${id}-${row.label}`,
            label: row.label,
            value: row.value,
          })),
      });
      return;
    }

    // Sono total: duração + início e fim. Nota/despertar ficam em seções próprias.
    if (code === "93832-4") {
      const { durationText, start, end } = getSleepTotalDisplay(resource);

      metrics.push({
        id: `${id}-duration`,
        label: "Sono total · Duração",
        value: durationText,
      });
      if (start) {
        metrics.push({
          id: `${id}-start`,
          label: "Sono total · Início",
          value: start,
        });
      }
      if (end) {
        metrics.push({
          id: `${id}-end`,
          label: "Sono total · Fim",
          value: end,
        });
      }
      return;
    }

    // Temperatura: mínima → média → máxima (com horário no valor quando existir)
    if (code === "81264-4") {
      const { minText, avgText, maxText, minTime, maxTime, avgTime } =
        getSleepSkinTempDisplay(resource);

      const withTime = (temp: string, time: string) =>
        time ? `${temp} · ${time}` : temp;

      metrics.push({
        id: `${id}-min`,
        label: "Temperatura corporal · Mínima",
        value: withTime(minText, minTime),
      });
      metrics.push({
        id: `${id}-avg`,
        label: "Temperatura corporal · Média",
        value: withTime(avgText, avgTime),
      });
      metrics.push({
        id: `${id}-max`,
        label: "Temperatura corporal · Máxima",
        value: withTime(maxText, maxTime),
      });
      return;
    }

    // SpO2: mínima (menor valor) e média — nunca duas linhas "Média"
    if (code === "59408-5") {
      const spo2Rows = getSpO2MetricRows(resource, id);
      if (spo2Rows.length > 0) {
        metrics.push(...spo2Rows);
        return;
      }
    }

    metrics.push({
      id,
      label,
      value: formatWearableObservationValue(resource),
    });
  });

  // Se SpO2 veio em duas Observations ambas como "Média", corrige pelo valor
  const fixedMetrics = fixDuplicateSpO2MediaLabels(metrics);

  // Ordem canônica (sono total, estágios, SpO2, temperatura, etc.)
  return { metrics: sortByMetricLabel(fixedMetrics), details };
}

/**
 * SpO2 (59408-5): mínima e média a partir do helper compartilhado.
 * O menor valor é sempre a mínima quando os rótulos do app não ajudam.
 */
function getSpO2MetricRows(resource: any, id: string): MetricRow[] {
  const { min, avg } = getSpO2DisplayParts(resource);
  const rows: MetricRow[] = [];

  if (min) {
    rows.push({
      id: `${id}-min`,
      label: "SpO2 · Mínima",
      value: min.formatted,
    });
  }

  if (avg) {
    rows.push({
      id: `${id}-avg`,
      label: "SpO2 · Média",
      value: avg.formatted,
    });
  }

  return rows;
}

/** Extrai o número do texto formatado ("97.0 %", "89 %", etc.). */
function parseMetricNumber(text: string): number | null {
  const match = String(text).replace(",", ".").match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const value = Number(match[0]);
  return Number.isFinite(value) ? value : null;
}

/**
 * Caso raro: duas Observations SpO2 no mesmo dia, ambas rotuladas "Média".
 * Renomeia a de menor valor para "Mínima".
 */
function fixDuplicateSpO2MediaLabels(metrics: MetricRow[]): MetricRow[] {
  const mediaIndexes: number[] = [];
  metrics.forEach((metric, index) => {
    if (metric.label.toLowerCase() === "spo2 · média") {
      mediaIndexes.push(index);
    }
  });

  if (mediaIndexes.length < 2) return metrics;

  const withValues = mediaIndexes
    .map((index) => ({
      index,
      value: parseMetricNumber(metrics[index].value),
    }))
    .filter(
      (item): item is { index: number; value: number } => item.value !== null
    );

  if (withValues.length < 2) return metrics;

  withValues.sort((a, b) => a.value - b.value);
  const next = [...metrics];
  next[withValues[0].index] = {
    ...next[withValues[0].index],
    label: "SpO2 · Mínima",
  };
  return next;
}
